"use client"
import Text from "@/app/(site)/_components/Atoms/Text"
import AddToCartButton from "@/app/(site)/_components/Modules/ProductDetail/AddToCartButton"
import ColorSwatches from "@/app/(site)/_components/Modules/ProductDetail/ColorSwatches"
import LensGuide from "@/app/(site)/_components/Modules/ProductDetail/LensGuide/LensGuide"
import SelectedSize from "@/app/(site)/_components/Modules/ProductDetail/SelectedSize/SelectedSize"
import SizeChart from "@/app/(site)/_components/Modules/ProductDetail/SizeChart"
import SizeGuide from "@/app/(site)/_components/Modules/ProductDetail/SizeGuide/SizeGuide"
import { useEyeFitUIEvent } from "@/app/(site)/_utils/_hooks/useEyeFitUIEvent"
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { selectSize, useAppDispatch } from "@/app/(site)/contexts/Global"
import { viewItemDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { viewItemEvent } from "@/lib/utils/tracking/gtmEvents"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"

import FitType from "@/app/(site)/_components/Modules/ProductDetail/FitType"
import {
    ProductData,
    RelatedVariant,
    SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"

type ProductFormType = {
    relatedVariantsFitType?: string[]
    productFitType?: string
    product: CheckoutAPIProduct | RelatedProduct | ProductData
    relatedVariants: RelatedProduct[] | RelatedVariant[]
    firstAvailableSize?: SizeInfo
    sizes: SizeInfo[]
    params: { slug: string; locale: string }
}

interface DataForEyeFitUIEventDetail {
    canonicalCategory: string
    gender: string
    sizeLabels: string[]
    mainImage: string
    productName: string
}

export default function ProductForm({
    relatedVariantsFitType,
    productFitType,
    product,
    relatedVariants,
    firstAvailableSize,
    sizes,
    params
}: ProductFormType) {
    const router = useRouter()
    const dispatch = useAppDispatch()
    const langCode = getLangCode(params.locale) || "en"
    const { functionalConsent } = useOneTrustOptInLoad()

    const [errorStyles, setErrorStyles] = useState(false)
    const [dataForEyeFitUI, setDataForEyeFitUI] = useState<DataForEyeFitUIEventDetail>()
    const [hasLoaded, setHasLoaded] = useState(false)
    const [fitType, setFitType] = useState(productFitType || "")

    const [filteredAllVariants, setFilteredAllVariants] = useState<
        (CheckoutAPIProduct | RelatedProduct | ProductData | RelatedVariant)[]
    >([...relatedVariants, product])

    const canonicalCategory = Array.isArray(product?.categoryName)
        ? product.categoryName.map(c => c.toLowerCase()).join("/")
        : ""

    const productColorName = product?.color_swatch?.color_name
    const productLensName = product?.lens?.lens_name
    const isFitType =
        Array.isArray(relatedVariantsFitType) && relatedVariantsFitType.length > 0
    const sizeLabels = sizes.map(size => size.name) || []

    useEffect(() => {
        if (!hasLoaded) {
            if (firstAvailableSize) {
                dispatch(selectSize(firstAvailableSize.name))
                viewItemEvent(product, firstAvailableSize)
                if (functionalConsent) {
                    viewItemDepictEvent(product, firstAvailableSize)
                }
                setHasLoaded(true)
            }
            setHasLoaded(true)
        }
    }, [firstAvailableSize, product.uri])

    useEffect(() => {
        if (product?.fit_type_fit_type !== fitType) {
            const relevantProduct = (relatedVariants as RelatedVariant[]).find(
                p => p?.fit_type_fit_type === fitType
            )
            const newUri = relevantProduct?.uri

            if (newUri) {
                router.replace(newUri, {
                    // @ts-expect-error: shallow is not in NavigateOptions
                    shallow: true
                })
            }
        }
    }, [product?.fit_type_fit_type, fitType])

    useEffect(() => {
        if (product?.categoryName && product.categoryName?.length > 0) {
            setDataForEyeFitUI({
                // Last category in the array is the most relevant
                canonicalCategory: canonicalCategory ?? "",
                gender: product?.fit?.[0]?.label ?? "",
                sizeLabels: sizeLabels ?? "",
                mainImage:
                    (product as ProductData)?.media?.standard?.[0]?.url ||
                    placeholder?.src,
                productName: product?.name ?? ""
            })
        }
    }, [product.productSku])

    useEyeFitUIEvent(dataForEyeFitUI || null)

    return (
        <form>
            {/* fit_type_fit_type */}
            {isFitType && (
                <div className="flex gap-8 border-t border-t-main-medium-grey py-16">
                    <Text size="small" weight="regular">
                        {t(langCode, "Fit")}:{" "}
                    </Text>
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {productFitType}
                    </Text>
                </div>
            )}

            {isFitType && (
                <FitType
                    relatedVariantsFitType={relatedVariantsFitType || []}
                    relatedVariants={relatedVariants}
                    setFilteredAllProducts={setFilteredAllVariants}
                    setFitType={setFitType}
                    fitType={fitType}
                />
            )}

            <div className="mb-16 border-b border-b-main-medium-grey pb-32">
                <div className="flex justify-between">
                    <div>
                        <div className="mb-15 flex grow-0 gap-4 sm:mb-8">
                            <Text size="small" weight="regular">
                                {t(langCode, "color")}:{" "}
                            </Text>
                            <Text
                                size="small"
                                weight="regular"
                                className="break-words text-main-dark-grey"
                            >
                                {productColorName || ""}
                            </Text>
                        </div>
                        {product?.lens && (
                            <div className="flex gap-4">
                                <Text size="small" weight="regular">
                                    {t(langCode, "lens")}:{" "}
                                </Text>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className="text-main-dark-grey"
                                >
                                    {productLensName || ""}
                                </Text>
                            </div>
                        )}
                    </div>
                    {product.lens_guide_type && <LensGuide />}
                </div>
                <div className="mt-16 flex items-center gap-8">
                    <ColorSwatches
                        initialProductValue={filteredAllVariants}
                        sizes={sizes}
                    />
                </div>
            </div>
            <div className="mb-16 border-b border-b-main-medium-grey pb-32 md:mb-32">
                <div className="mb-16 flex justify-between">
                    <SelectedSize initialSelectedSize={firstAvailableSize?.name} />
                    {product.size_guide_type && <SizeGuide />}
                </div>
                <div className="mb-13 flex gap-16">
                    <SizeChart
                        sizes={sizes}
                        initialSizeValue={firstAvailableSize?.name}
                        setErrorStyles={setErrorStyles}
                        errorStyles={errorStyles}
                    />
                </div>
            </div>
            <div className="pb-32">
                <AddToCartButton setErrorStyles={setErrorStyles} product={product} />
            </div>
        </form>
    )
}
